import { showNotification } from '@helpers/notification-helper';
import { addGuarantor, createProvider } from '@services/external/provider.api';
import { Button, Col, Form, Row } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { CreateProviderParams } from 'types/provider.types';
import ContactForm from './components/contact-form';
import GeneralForm from './components/general-form';
import GuarantorForm from './components/guarantor-form';
import PaymentDetailsForm from './components/payment-details-form';
import ServicesForm from './components/services-form';
import { initialValues, validationSchema } from './provider-create-form.schema';
import { FormSC } from './provider-create-form.styled';

interface ProviderCreateProps {
  afterSubmit?: () => void;
  canGoBack?: boolean;
}

const ProviderCreateForm: React.FunctionComponent<ProviderCreateProps> = ({
  afterSubmit,
  canGoBack = true,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [, formProps] = Form.useForm({
    initialValues,
    validationSchema,
    onSubmit: async (v, h) => {
      try {
        const formDataValues = { ...v } as CreateProviderParams;
        // @ts-expect-error
        delete formDataValues.guarantor;

        const { data } = await createProvider(formDataValues);

        if (v.servicesType === 'withGuarantor' && v.guarantor) {
          await addGuarantor(data.data.providerId, v.guarantor);
        }

        afterSubmit?.();
      } catch (error) {
        // TODO: handle errors when on BE
        console.log(error);
        showNotification({
          title: formatMessage({ id: 'provider.onboarding.form.error' }),
          copy: '',
          type: 'ERROR',
        });
      }
    },
  });

  return (
    <FormSC {...formProps} showFeedback={false}>
      <GeneralForm />
      <ContactForm />
      <PaymentDetailsForm />
      <ServicesForm />
      <GuarantorForm />
      <Form.Custom>
        {(f) => (
          <Row gutter={[16, 0]} style={{ marginTop: theme.spacings.component.L }}>
            <Col xs={6}>
              {canGoBack ? (
                <Button
                  variant="secondary"
                  block={false}
                  leftArrow
                  onClick={() => history.goBack()}
                >
                  {formatMessage({ id: 'provider.onboarding.form.back' })}
                </Button>
              ) : null}
            </Col>
            <Col xs={6}>
              <Button type="submit" loading={f.isSubmitting}>
                {formatMessage({ id: 'provider.onboarding.form.create' })}
              </Button>
            </Col>
          </Row>
        )}
      </Form.Custom>
    </FormSC>
  );
};

export default React.memo(ProviderCreateForm);
